import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { logger } from '../utils/logger';
import { taggedGenre } from '../utils/genreNormalization';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { userService, PublicUserProfile } from '../services/userService';
import { Stream } from '../services/streams';
import { useAudioPlayer, Stream as PlayerStream } from '../contexts/AudioPlayerContext';
import { useAuthPrompt } from '../contexts/AuthPromptContext';
import './Profile.css';

const PublicProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();

  // Audio player for playback functionality
  const { playStream } = useAudioPlayer();
  const { requireAuth } = useAuthPrompt();

  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [streamsLoading, setStreamsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<PublicUserProfile | null>(null);

  // Convert Stream service type to Player stream type
  const toPlayerStream = (stream: Stream): PlayerStream => ({
    streamId: stream.streamId,
    name: stream.name,
    description: stream.description,
    audioUrl: stream.audioUrl,
    coverImage: stream.coverImage,
    uploaderUsername: stream.uploaderUsername,
    uploaderDisplayName: stream.uploaderDisplayName,
    waveformData: stream.waveformData,
  });

  // Handle play stream with auth check
  const handlePlayStream = (e: React.MouseEvent, stream: Stream) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth('play', stream.name)) return;
    playStream(toPlayerStream(stream));
  };

  const loadProfile = useCallback(async () => {
    if (!username) return;

    try {
      setLoading(true);
      setError(null);
      const data = await userService.getPublicProfile(username);
      setProfile(data);
    } catch (err: any) {
      logger.error('Error loading profile:', err);
      setError(err.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [username]);

  const loadStreams = useCallback(async () => {
    if (!username) return;

    try {
      setStreamsLoading(true);
      const data = await userService.getUserUploads(username);
      setStreams(data);
    } catch (err: any) {
      logger.error('Error loading streams:', err);
    } finally {
      setStreamsLoading(false);
    }
  }, [username]);

  const loadCurrentUserProfile = useCallback(async () => {
    if (!user) return;

    try {
      const profile = await userService.getProfile();
      setCurrentUserProfile(profile);
    } catch (err) {
      logger.error('Error loading current user profile:', err);
    }
  }, [user]);

  const loadFollowStatus = useCallback(async () => {
    if (!user || !username) return;

    try {
      const { isFollowing } = await userService.checkFollowStatus(username);
      setIsFollowing(isFollowing);
    } catch (err) {
      logger.error('Error loading follow status:', err);
    }
  }, [user, username]);

  useEffect(() => {
    loadProfile();
    loadStreams();
    loadCurrentUserProfile();
  }, [loadProfile, loadStreams, loadCurrentUserProfile]);

  useEffect(() => {
    loadFollowStatus();
  }, [loadFollowStatus]);

  const handleFollow = async () => {
    if (!user || !username) {
      navigate('/login');
      return;
    }

    try {
      setFollowLoading(true);
      await userService.followUser(username);
      setIsFollowing(true);

      if (profile) {
        setProfile({ ...profile, followerCount: profile.followerCount + 1 });
      }
    } catch (err: any) {
      logger.error('Error following user:', err);
      alert('Failed to follow user. Please try again.');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!user || !username) return;

    try {
      setFollowLoading(true);
      await userService.unfollowUser(username);
      setIsFollowing(false);

      if (profile) {
        setProfile({ ...profile, followerCount: Math.max(0, profile.followerCount - 1) });
      }
    } catch (err: any) {
      logger.error('Error unfollowing user:', err);
      alert('Failed to unfollow user. Please try again.');
    } finally {
      setFollowLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="profile-loading">
          <div className="profile-loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container">
        <div className="profile-error">
          <h2 className="profile-error-title">User Not Found</h2>
          <p className="profile-error-text">
            {error || 'The user you are looking for does not exist.'}
          </p>
          <Link to="/" className="profile-error-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUserProfile && currentUserProfile.username === profile.username;

  return (
    <div className="container" style={{ padding: '2rem 1rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Hero Section */}
      <div className="profile-hero">
        <div className="profile-hero-content">
          {/* Profile Picture */}
          <div className="profile-avatar-wrapper">
            {profile.profilePictureUrl ? (
              <img
                src={profile.profilePictureUrl}
                alt={profile.displayName || profile.username}
                className="profile-avatar"
              />
            ) : (
              <div className="profile-avatar-placeholder">
                {(profile.displayName?.[0] || profile.username[0]).toUpperCase()}
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="profile-info">
            <h1 className="profile-name">
              {profile.displayName || profile.username}
            </h1>
            <p className="profile-username">@{profile.username}</p>

            {profile.bio && (
              <p className="profile-bio">{profile.bio}</p>
            )}

            {/* Stats */}
            <div className="profile-stats">
              <div className="profile-stat">
                <span className="profile-stat-value">{profile.streamCount}</span>
                <span className="profile-stat-label">Streams</span>
              </div>
              <Link to={`/users/${profile.username}/followers`} className="profile-stat-link">
                <div className="profile-stat">
                  <span className="profile-stat-value">{profile.followerCount}</span>
                  <span className="profile-stat-label">Followers</span>
                </div>
              </Link>
              <Link to={`/users/${profile.username}/following`} className="profile-stat-link">
                <div className="profile-stat">
                  <span className="profile-stat-value">{profile.followingCount}</span>
                  <span className="profile-stat-label">Following</span>
                </div>
              </Link>
            </div>

            {/* Follow Button */}
            {!isOwnProfile && user && (
              <button
                onClick={isFollowing ? handleUnfollow : handleFollow}
                disabled={followLoading}
                className={`profile-follow-btn ${isFollowing ? 'following' : 'follow'}`}
              >
                {followLoading ? (
                  'Loading...'
                ) : isFollowing ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Following
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Follow
                  </>
                )}
              </button>
            )}

            {/* Login to Follow */}
            {!user && (
              <button
                onClick={() => navigate('/login')}
                className="profile-follow-btn follow"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Follow
              </button>
            )}

            <p className="profile-member-since">
              Member since {formatDate(profile.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Streams Section */}
      <div>
        <div className="profile-section-header">
          <h2 className="profile-section-title">
            Streams
            <span className="profile-section-count">({profile.streamCount})</span>
          </h2>
        </div>

        {streamsLoading ? (
          <div className="profile-loading">
            <div className="profile-loading-spinner"></div>
            <p>Loading streams...</p>
          </div>
        ) : streams.length === 0 ? (
          <div className="profile-empty-state">
            <svg className="profile-empty-icon" viewBox="0 0 120 120" fill="none">
              <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
              <circle cx="60" cy="60" r="35" stroke="currentColor" strokeWidth="2" opacity="0.4"/>
              <circle cx="60" cy="60" r="20" stroke="currentColor" strokeWidth="2" opacity="0.6"/>
              <path d="M52 48L72 60L52 72V48Z" fill="currentColor" opacity="0.5"/>
            </svg>
            <h3 className="profile-empty-title">No streams yet</h3>
            <p className="profile-empty-text">
              {profile.displayName || profile.username} hasn't uploaded any streams yet.
            </p>
          </div>
        ) : (
          <div className="profile-streams-grid">
            {streams.map((stream) => (
              <Link
                key={stream.streamId}
                to={`/streams/${stream.streamId}`}
                className="profile-stream-card"
              >
                <div className="profile-stream-cover-wrapper">
                  {stream.coverImage ? (
                    <img
                      src={stream.coverImage}
                      alt={stream.name}
                      className="profile-stream-cover"
                    />
                  ) : (
                    <div className="profile-stream-cover-placeholder">
                      <svg width="32" height="32" viewBox="0 0 64 64" fill="none">
                        <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="2"/>
                        <path d="M26 24L40 32L26 40V24Z" fill="currentColor"/>
                      </svg>
                    </div>
                  )}
                  <div className="profile-stream-play-overlay">
                    <button
                      className="profile-stream-play-btn"
                      onClick={(e) => handlePlayStream(e, stream)}
                      title="Play"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7L8 5z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="profile-stream-info">
                  <h3 className="profile-stream-name">{stream.name}</h3>
                  <p className="profile-stream-genre">{taggedGenre(stream.genre)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;
